import {
  keepPreviousQueryData,
  useAuthStore,
  useBuyerAppConfig,
  useInfiniteQuery,
  useQueryClient,
} from '@northladder/services';

import { BIDS_API } from '../bidsAPIClient';
import {
  IFetchAllBidsData,
  IFetchAllBidsPayload,
  ISearchBidsOptions,
} from '../types';

/**
 * This is to track the bids listing key in react query cache.
 */
export const FETCH_ALL_BIDS_KEY = 'fetch-all-bids-key';

/**
 * This is used to perform query invalidation for bids listing
 */
export const useBidsListingQueryInvalidation = () => {
  const queryClient = useQueryClient();

  const invalidateBidsListing = () => {
    queryClient.invalidateQueries({
      queryKey: [FETCH_ALL_BIDS_KEY],
    });
  };

  return {
    invalidateBidsListing,
  };
};

export interface IUseAllBidsSuccessCallbackProps {
  pages: IFetchAllBidsData[];
}

/**
 * Adds conditional filters to generate an optimally formatted payload for the
 * bids search results.
 */
export const formatSearchBidsQueryPayload = (payload: IFetchAllBidsPayload) => {
  const {
    bidFilterKey,
    brandId,
    brandList,
    categoryId,
    count,
    filterBy,
    fromDate,
    groupIds,
    isHotDeal,
    orderBy,
    search,
    sortBy,
    toDate,
  } = payload;

  const localPayload = {
    count,
    ...(bidFilterKey ? { bidFilterKey } : {}),
    ...(brandId && brandId !== 'all' ? { brandId } : {}),
    ...(brandList && brandList.length > 0 ? { brandList } : {}),
    ...(categoryId ? { categoryId } : {}),
    ...(filterBy ? { filterBy } : {}),
    ...(fromDate ? { fromDate } : {}),
    ...(groupIds && groupIds.length > 0 ? { groupIds } : {}),
    ...(isHotDeal ? { isHotDeal } : {}),
    ...(orderBy ? { orderBy } : {}),
    ...(sortBy ? { sortBy } : {}),
    ...(search ? { search } : {}),
    ...(toDate ? { toDate } : {}),
  };

  return localPayload;
};

type TUseSearchBidsQueryOptions = Pick<
  ISearchBidsOptions,
  'payload' | 'urlParams'
>;

/**
 * This performs a get request and retrieves the bids data by category and
 * brands.
 * Provides data fetching states to be used in the bids table.
 */
export const useSearchBidsQuery = (params: TUseSearchBidsQueryOptions) => {
  const { payload, urlParams } = params;
  const { isRankOneBidDisplayEnabled } = useBuyerAppConfig().bids;

  // TODO: Create a custom useInfinite query and update the key into there
  const userId = useAuthStore().activeSession?.userId || '';

  const localPayload = formatSearchBidsQueryPayload(payload);
  const { fromDate, isHotDeal, toDate, groupIds } = urlParams || {};

  const {
    bidFilterKey,
    brandId,
    brandList,
    categoryId,
    count,
    filterBy,
    orderBy,
    search,
    sortBy,
  } = localPayload;

  const {
    data,
    error,
    hasNextPage,
    fetchNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isPending,
    isPaused,
    refetch,
  } = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      FETCH_ALL_BIDS_KEY,
      bidFilterKey,
      brandId,
      brandList,
      categoryId,
      filterBy,
      fromDate,
      groupIds,
      isHotDeal,
      orderBy,
      search,
      sortBy,
      toDate,
      userId,
    ],
    networkMode: 'always', // Even make calls when offline
    queryFn: ({ pageParam = 1 }) => {
      const queryPayload: ISearchBidsOptions = {
        isRankOneBidDisplayEnabled,
        payload: { ...localPayload, page: pageParam },
        urlParams,
      };
      return BIDS_API.searchAllBids(queryPayload);
    },
    initialPageParam: 1,
    placeholderData: keepPreviousQueryData,
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1;
      const currentAssetCount = allPages.length * count;

      return currentAssetCount < lastPage.totalCount ? nextPage : undefined;
    },
  });

  return {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isPaused,
    isPending,
    refetch,
  };
};
